import React, { useState, useEffect } from 'react';
import { Battery, Zap, Wind, Leaf, ArrowRight, CheckCircle2, Users, Award, TrendingUp } from 'lucide-react';
import Lottie from "lottie-react";
import animationData from "../../assets/Green Energy Concept Animation.json";
import { Link } from "react-router-dom";

export default function GreenEnergyLanding() {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const sectors = [
        {
            title: "Solar Energy",
            description: "Master photovoltaic systems, installation techniques, and grid integration for the solar revolution.",
            skills: ["System Design", "Installation", "Grid Integration"],
            route: "/training/solar"
        },
        {
            title: "Green Hydrogen",
            description: "Learn electrolysis technologies and renewable power integration for clean fuel production.",
            skills: ["Electrolysis Tech", "Power Integration", "Storage Systems"],
            route: "/training/green-hydrogen"
        },
        {
            title: "E-Mobility",
            description: "Develop expertise in EV infrastructure, charging systems, and sustainable transportation.",
            skills: ["EV Infrastructure", "Charging Tech", "Battery Management"],
            route: "/training/electric-vehicles"
        }
    ];

    const stats = [
        { icon: <Users />, value: "10,000+", label: "Skills Gap" },
        { icon: <TrendingUp />, value: "300%", label: "Growth Rate" },
        { icon: <Award />, value: "OSHA", label: "Compliant" }
    ];

    return (
        <div className="min-h-screen bg-[#FFFAE5]">
            {/* Hero Section */}
            <section className="relative">
                <div className="max-w-7xl mx-auto px-6 pt-32 pb-16 md:pt-48 md:pb-32">
                    <div className={`transition-all duration-500 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
                        <div className="grid md:grid-cols-2 gap-10 items-center">
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <Leaf className="w-6 h-6 text-[#D97706]" />
                                    <span className="text-[#D97706] font-bold text-sm uppercase tracking-wider">
                                        Green Skills Revolution
                                    </span>
                                </div>
                                <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                                    Bridging the <span className="text-[#D97706]">Green Skills Gap</span>
                                </h1>
                                <p className="text-lg text-gray-700 max-w-2xl mb-8 leading-relaxed">
                                    The global transition to clean energy is accelerating. We transform the challenge of green skill shortages into professional opportunities.
                                </p>
                                <Link to="/training/upcoming">
                                    <button className="bg-[#D97706] text-white px-8 py-3.5 rounded-full font-bold text-base hover:bg-[#B45309] transition-colors duration-200 shadow-md flex items-center gap-2 group active:scale-[0.97]">
                                        View Programs
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                                    </button>
                                </Link>
                            </div>
                            <div className="w-full flex justify-center">
                                <Lottie animationData={animationData} loop autoplay className="w-full max-w-sm" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="bg-white/60">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col sm:flex-row items-stretch divide-y sm:divide-y-0 sm:divide-x divide-orange-100">
                        {stats.map((stat, index) => (
                            <div key={index} className="flex-1 py-8 md:py-10 px-6 md:px-10 text-center">
                                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-1 tracking-tight leading-none">
                                    {stat.value}
                                </div>
                                <div className="flex items-center justify-center gap-2 mt-2">
                                    <div className="text-[#D97706] bg-orange-50 p-1.5 rounded-lg">
                                        {stat.icon}
                                    </div>
                                    <span className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-widest">{stat.label}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Sectors Section */}
            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12 md:mb-16">
                        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 tracking-tight">
                            Master Critical <span className="text-[#D97706]">Green Sectors</span>
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Specialized training in the technologies shaping our sustainable future.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
                        {sectors.map((sector, index) => {
                            const routes = ["/training/solar", "/training/green-hydrogen", "/training/electric-vehicles"];
                            return (
                                <div key={index} className="group bg-white rounded-2xl p-6 md:p-10 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-200 flex flex-col h-full">
                                    <div className="text-[11px] font-black text-amber-500/60 uppercase tracking-[0.2em] mb-3">Sector 0{index + 1}</div>

                                    <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-3 tracking-tight group-hover:text-[#D97706] transition-colors duration-200">{sector.title}</h3>
                                    <p className="text-gray-600 mb-6 md:mb-8 text-sm md:text-base leading-relaxed font-medium">{sector.description}</p>

                                    <div className="grid grid-cols-1 gap-3 mb-6 md:mb-10 flex-grow">
                                        {sector.skills.map((skill, idx) => (
                                            <div key={idx} className="flex items-center gap-3 text-[12px] md:text-[13px] font-bold text-gray-700 py-2.5 md:py-3 px-4 md:px-5 bg-amber-50 rounded-xl">
                                                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-amber-500 flex-shrink-0" />
                                                <span>{skill}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <Link to={routes[index]} className="mt-auto">
                                        <button className="w-full bg-[#D97706] text-white px-6 py-3.5 md:px-8 md:py-4 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-[0.2em] hover:bg-[#B45309] transition-colors duration-200 active:scale-[0.97] flex items-center justify-center gap-2">
                                            <span>Explore Sector</span>
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                                        </button>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-[#D97706]">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight">
                        Ready to Build Your Green Career?
                    </h2>
                    <p className="text-lg text-white/90 mb-10">
                        Join individuals mastering the skills that power our sustainable future.
                    </p>
                    <Link to="/training/contactform">
                        <button className="bg-white text-[#D97706] px-10 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all duration-200 active:scale-[0.97]">
                            Get Started Today
                        </button>
                    </Link>
                </div>
            </section>
        </div>
    );
}
